// ============================================================================
// PairsForm — Pad de signature manuscrite réutilisable (canvas)
// ============================================================================
// Capture une signature au doigt/souris et la renvoie en PNG (data URL) via
// `onSign`. Utilisé par la page d'émargement publique (lien token) et par
// l'émargement direct in-app (dialogue de la page Séance).
// ============================================================================

import { useRef, useState, useEffect, useCallback } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { Eraser, CheckCircle } from "lucide-react";

export default function SignaturePad({
  onSign,
  submitting = false,
  submitLabel = "Signer ma présence",
  height = 200,
}: {
  onSign: (dataUrl: string) => void | Promise<void>;
  submitting?: boolean;
  submitLabel?: string;
  height?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const lastPt = useRef<{ x: number; y: number } | null>(null);
  const [hasDrawn, setHasDrawn] = useState(false);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1E4A43";
    }
  }, []);

  useEffect(() => {
    const id = requestAnimationFrame(initCanvas);
    return () => cancelAnimationFrame(id);
  }, [initCanvas]);

  function pointFrom(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }
  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    e.preventDefault();
    canvasRef.current?.setPointerCapture(e.pointerId);
    drawing.current = true;
    lastPt.current = pointFrom(e);
  }
  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx || !lastPt.current) return;
    const p = pointFrom(e);
    ctx.beginPath();
    ctx.moveTo(lastPt.current.x, lastPt.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPt.current = p;
    if (!hasDrawn) setHasDrawn(true);
  }
  function onPointerUp() {
    drawing.current = false;
    lastPt.current = null;
  }
  function effacer() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  }
  function valider() {
    if (!hasDrawn || !canvasRef.current) return;
    void onSign(canvasRef.current.toDataURL("image/png"));
  }

  return (
    <Box>
      <Box sx={{ border: "2px dashed #c7c7d1", borderRadius: 2, bgcolor: "#fff", touchAction: "none", height, mb: 1 }}>
        <canvas
          ref={canvasRef}
          style={{ width: "100%", height: "100%", display: "block", cursor: "crosshair" }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerLeave={onPointerUp}
        />
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1 }}>
        <Button startIcon={<Eraser size={16} />} onClick={effacer} disabled={!hasDrawn || submitting} sx={{ textTransform: "none" }}>
          Effacer
        </Button>
        <Button
          variant="contained"
          onClick={valider}
          disabled={!hasDrawn || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <CheckCircle size={16} />}
          sx={{ textTransform: "none" }}
        >
          {submitLabel}
        </Button>
      </Box>
    </Box>
  );
}
